import React, { useEffect, useRef, useState } from 'react'
import { SampleUser } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import './createGroup.css'
import './addExpense.css'

// Wired to POST /splitwise/groups/. "Link Bookings" is wired to
// GET /splitwise/bookings/ (a plain list, no booking_ids query needed —
// separate from the /bookings/guests/ helper). "Add Members" takes manual
// name+email pairs per the real API (there's no contact-search endpoint) —
// they're created as pending until they join via the invite link.
const BackArrow = () => (
    <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#1D2A4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const CameraIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" stroke="#9CA3AF" strokeWidth="1.4" strokeLinejoin="round" />
        <circle cx="12" cy="13.5" r="3.2" stroke="#9CA3AF" strokeWidth="1.4" />
    </svg>
)

// The real booking objects' shape isn't confirmed yet — falls back across a
// few plausible field names until the user shares an actual response.
const bookingLabel = (booking) => {
    const title = booking.title || booking.name || booking.service_name || booking.booking_number || `Booking #${booking.id}`
    const meta = booking.date_label || booking.date || booking.service || ''
    return meta ? `${title} · ${meta}` : title
}

const CreateGroup = ({ onBack, onCreated }) => {
    const fileInputRef = useRef(null)
    const [photoFile, setPhotoFile] = useState(null)
    const [photoPreview, setPhotoPreview] = useState('')
    const [groupName, setGroupName] = useState('')
    const [tag, setTag] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const [bookings, setBookings] = useState([])
    const [bookingsLoading, setBookingsLoading] = useState(true)
    const [bookingIds, setBookingIds] = useState([])

    const [memberRows, setMemberRows] = useState([{ name: '', email: '' }])

    const [guests, setGuests] = useState([])
    const [guestsLoading, setGuestsLoading] = useState(false)

    useEffect(() => {
        service
            .get(endpoints.splitwise.bookings, {}, { silent: true })
            .then(({ data }) => setBookings(Array.isArray(data.data) ? data.data : []))
            .catch(() => setBookings([]))
            .finally(() => setBookingsLoading(false))
    }, [])

    // Pull the guest list for whatever bookings are currently linked, so
    // the user can one-click add them as members instead of retyping
    // name+email by hand.
    useEffect(() => {
        if (!bookingIds.length) { setGuests([]); return }
        setGuestsLoading(true)
        service
            .get(endpoints.splitwise.bookingGuests, { booking_ids: bookingIds.join(',') }, { silent: true })
            .then(({ data }) => setGuests(Array.isArray(data.data) ? data.data : []))
            .catch(() => setGuests([]))
            .finally(() => setGuestsLoading(false))
    }, [bookingIds])

    const selectedBookings = bookings.filter((booking) => bookingIds.includes(booking.id))
    const availableBookings = bookings.filter((booking) => !bookingIds.includes(booking.id))

    const addBooking = (id) => {
        if (!id) return
        const numId = Number(id)
        if (bookingIds.includes(numId)) return
        setBookingIds((prev) => [...prev, numId])
    }

    const removeBooking = (id) => {
        setBookingIds((prev) => prev.filter((bid) => bid !== id))
    }

    const updateMemberRow = (index, field, value) => {
        setMemberRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
    }

    const addMemberRow = () => setMemberRows((prev) => [...prev, { name: '', email: '' }])
    const removeMemberRow = (index) => setMemberRows((prev) => prev.filter((_, i) => i !== index))

    const isGuestAdded = (guest) => memberRows.some((row) => row.email.trim().toLowerCase() === guest.email.toLowerCase())

    const addGuestAsMember = (guest) => {
        if (isGuestAdded(guest)) return
        setMemberRows((prev) => {
            const filled = prev.filter((row) => row.name.trim() || row.email.trim())
            return [...filled, { name: guest.name, email: guest.email }]
        })
    }

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setPhotoFile(file)
        const reader = new FileReader()
        reader.onload = () => setPhotoPreview(reader.result)
        reader.readAsDataURL(file)
    }

    const validMembers = memberRows
        .map((row) => ({ name: row.name.trim(), email: row.email.trim() }))
        .filter((row) => row.name && row.email)

    const canCreate = Boolean(groupName.trim()) && !submitting

    const handleCreate = () => {
        if (!canCreate) return
        setSubmitting(true)
        const payload = new FormData()
        payload.append('name', groupName.trim())
        if (tag.trim()) payload.append('tag', tag.trim())
        if (photoFile) payload.append('icon', photoFile)
        if (bookingIds.length) payload.append('booking_ids', JSON.stringify(bookingIds))
        if (validMembers.length) payload.append('members', JSON.stringify(validMembers))

        service
            .post(endpoints.splitwise.groups, payload)
            .then(() => onCreated())
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSubmitting(false))
    }

    return (
        <div>
            <div className='createGroupHead'>
                <button type='button' className='createGroupHead__back' onClick={onBack}>
                    <BackArrow />
                </button>
                <div>
                    <div className='createGroupHead__title'>Create Group</div>
                    <div className='createGroupHead__subtitle'>Plan, chat and split expenses with your group.</div>
                </div>
            </div>

            <div className='createGroupCard'>
                <div className='createGroupPhoto'>
                    <span
                        className='createGroupPhoto__circle'
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {photoPreview ? <img src={photoPreview} alt="" /> : <CameraIcon />}
                    </span>
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='image/*'
                        style={{ display: 'none' }}
                        onChange={handlePhotoChange}
                    />
                    <span className='createGroupPhoto__link' onClick={() => fileInputRef.current?.click()}>Upload Photo</span>
                </div>

                <div className='createGroupField'>
                    <label>Group Name</label>
                    <input
                        type='text'
                        placeholder='e.g. Goa Trip 2026'
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                    />
                </div>

                <div className='createGroupField'>
                    <label>Tag <span className='createGroupField__optional'>(optional)</span></label>
                    <input
                        type='text'
                        placeholder='e.g. Goa'
                        value={tag}
                        onChange={(e) => setTag(e.target.value)}
                    />
                </div>

                <div className='createGroupField'>
                    <label>Link Bookings <span className='createGroupField__optional'>(optional)</span></label>
                    <div className='createGroupField__hint'>Attach confirmed bookings so they show up in this group.</div>
                    {bookingsLoading ? (
                        <p className='bsFareNote'>Loading your bookings...</p>
                    ) : (
                        <select
                            value=''
                            onChange={(e) => addBooking(e.target.value)}
                            disabled={!availableBookings.length}
                        >
                            <option value=''>{availableBookings.length ? 'Select a booking to add' : (bookings.length ? 'All bookings added' : 'No confirmed bookings found')}</option>
                            {availableBookings.map((booking) => (
                                <option key={booking.id} value={booking.id}>{bookingLabel(booking)}</option>
                            ))}
                        </select>
                    )}
                    {selectedBookings.length > 0 && (
                        <div className='createGroupChips' style={{ marginTop: 12 }}>
                            {selectedBookings.map((booking) => (
                                <span className='createGroupChip' key={booking.id}>
                                    {bookingLabel(booking)}
                                    <button type='button' className='createGroupChip__remove' onClick={() => removeBooking(booking.id)}>×</button>
                                </span>
                            ))}
                        </div>
                    )}

                    {bookingIds.length > 0 && (
                        <div style={{ marginTop: 16 }}>
                            <div className='createGroupField__hint' style={{ marginTop: 0, marginBottom: 8 }}>Guests from these bookings — tap to add as a member</div>
                            {guestsLoading ? (
                                <p className='bsFareNote'>Loading guests...</p>
                            ) : guests.length ? (
                                <div className='createGroupGuests'>
                                    {guests.map((guest) => {
                                        const added = isGuestAdded(guest)
                                        return (
                                            <div className='createGroupGuest' key={guest.id} onClick={() => addGuestAsMember(guest)}>
                                                <img src={guest.image || SampleUser} alt="" className='createGroupGuest__avatar' />
                                                <div className='createGroupGuest__info'>
                                                    <div className='createGroupGuest__name'>{guest.name}</div>
                                                    <div className='createGroupGuest__email'>{guest.email}</div>
                                                </div>
                                                <span className={`createGroupGuest__add ${added ? 'added' : ''}`}>
                                                    {added ? '✓ Added' : '+ Add'}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                            ) : (
                                <p className='bsFareNote'>No guests found for these bookings.</p>
                            )}
                        </div>
                    )}
                </div>

                <div className='createGroupField'>
                    <label>Add Members <span className='createGroupField__optional'>(optional)</span></label>
                    <div className='createGroupField__hint'>They'll be added as pending until they join via the invite link.</div>
                    {memberRows.map((row, index) => (
                        <div className='addExpensePage__row' key={index}>
                            <div className='addExpensePage__field'>
                                <label>Name</label>
                                <input type='text' value={row.name} onChange={(e) => updateMemberRow(index, 'name', e.target.value)} />
                            </div>
                            <div className='addExpensePage__field'>
                                <label>Email</label>
                                <input type='text' value={row.email} onChange={(e) => updateMemberRow(index, 'email', e.target.value)} />
                            </div>
                            {memberRows.length > 1 && (
                                <button type='button' className='addExpensePage__cancelBtn' style={{ marginTop: 26 }} onClick={() => removeMemberRow(index)}>×</button>
                            )}
                        </div>
                    ))}
                    <span className='createGroupPhoto__link' onClick={addMemberRow}>+ Add another person</span>
                </div>

                <div className='createGroupSummary'>
                    <div>Group Name: <span>{groupName || '—'}</span></div>
                    {tag.trim() && <div>Tag: <span>{tag.trim()}</span></div>}
                    {bookingIds.length > 0 && <div>Bookings: <span>{bookingIds.length} linked</span></div>}
                    {validMembers.length > 0 && <div>Members: <span>{validMembers.length} added</span></div>}
                </div>

                <button type='button' className='createGroupSubmitBtn' disabled={!canCreate} onClick={handleCreate}>
                    {submitting ? 'Creating...' : 'Create Group'}
                </button>
            </div>
        </div>
    )
}

export default CreateGroup
